import React, { useState, useEffect } from 'react';
import ClientUsageCharts from './ClientUsageCharts';
import ClientSaludPanel from './ClientSaludPanel';

export default function ClientShow({ clientId, onClose }) {
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('uso');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch(`/settings/clients_v2/${clientId}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(j => { if (!cancelled) { setClient(j); setLoading(false); } })
      .catch(e => { if (!cancelled) { setError(e.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [clientId]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="cv2-drawer__overlay" onClick={onClose}>
      <aside className="cv2-drawer" onClick={e => e.stopPropagation()}>
        <header className="cv2-drawer__header">
          <div className="cv2-drawer__header-left">
            {client?.logo && <img src={client.logo} alt="" className="cv2-drawer__logo" />}
            <div>
              <h3 className="cv2-drawer__title">{client?.company_name || '…'}</h3>
              <p className="cv2-drawer__subtitle">
                NIT: <span>{client?.nit || '—'}</span>
                {client?.plan && <> · Plan: <span>{client.plan.nick_name || client.plan.name}</span></>}
              </p>
            </div>
          </div>
          <button className="cv2-drawer__close" onClick={onClose}>
            <i className="fas fa-times" />
          </button>
        </header>

        <nav className="cv2-drawer__tabs">
          {['uso', 'info', 'plan', 'sarlaft', 'taimes', 'auditoria', 'modulos', 'salud'].map(t => (
            <button
              key={t}
              className={`cv2-tab ${activeTab === t ? 'cv2-tab--active' : ''}`}
              onClick={() => setActiveTab(t)}
            >
              {tabLabel(t)}
            </button>
          ))}
        </nav>

        <div className="cv2-drawer__body">
          {loading && <div className="cv2-loading"><i className="fas fa-spinner fa-spin" /> Cargando…</div>}
          {error && <div className="cv2-error">Error: {error}</div>}

          {client && !loading && (
            <>
              {activeTab === 'uso' && <ClientUsageCharts clientId={clientId} client={client} />}
              {activeTab === 'info' && <ClientInfoPanel client={client} />}
              {activeTab === 'plan' && <ClientPlanPanel client={client} />}
              {activeTab === 'sarlaft' && <ClientSarlaftPanel client={client} />}
              {activeTab === 'taimes' && <ClientTaimesPanel client={client} clientId={clientId} onUpdated={(patch) => setClient(c => ({ ...c, ...patch }))} />}
              {activeTab === 'auditoria' && <ClientAuditPanel client={client} clientId={clientId} onUpdated={(patch) => setClient(c => ({ ...c, ...patch }))} />}
              {activeTab === 'modulos' && <ClientModulesPanel client={client} />}
              {activeTab === 'salud' && <ClientSaludPanel client={client} clientId={clientId} />}
            </>
          )}
        </div>
      </aside>
    </div>
  );
}

function tabLabel(t) {
  const map = { uso: '📊 Uso', info: 'ℹ️ Info', plan: '💳 Plan', sarlaft: '🛡️ SARLAFT', taimes: '🤖 Taimes', auditoria: '📝 Auditoría', modulos: '🧩 Módulos', salud: '🏥 Gestión Salud' };
  return map[t] || t;
}

// Auditoría de ediciones de registros (Fordata) por tenant. Con el flag apagado
// sigue mandando el gate por formato con vista calendario activada.
function ClientAuditPanel({ client, clientId, onUpdated }) {
  const [enabled, setEnabled] = useState(!!client.survey_audit_enabled);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const save = () => {
    setSaving(true);
    setMsg(null);
    const token = document.querySelector('[name="csrf-token"]')?.content;
    fetch(`/settings/clients_v2/${clientId}/audit`, {
      method: 'PATCH',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': token, Accept: 'application/json' },
      body: JSON.stringify({ survey_audit_enabled: enabled }),
    })
      .then(r => r.json().then(j => ({ ok: r.ok, j })))
      .then(({ ok, j }) => {
        if (ok && j.success) {
          setMsg({ type: 'ok', text: 'Guardado.' });
          if (onUpdated) onUpdated({ survey_audit_enabled: j.survey_audit_enabled });
        } else {
          setMsg({ type: 'err', text: (j && j.error) || 'No se pudo guardar.' });
        }
      })
      .catch(() => setMsg({ type: 'err', text: 'No se pudo guardar.' }))
      .finally(() => setSaving(false));
  };

  return (
    <div className="cv2-taimes">
      <h4 className="cv2-panel-title">Auditoría de ediciones de registros</h4>
      <p className="cv2-taimes__hint">
        Registra el historial de cambios (quién editó qué y cuándo) de los registros ya guardados
        de <strong>todos los formatos</strong> de este cliente. Con el switch apagado, solo se audita
        en los formatos que tienen la vista calendario activada.
      </p>

      <label className="cv2-taimes__toggle">
        <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} />
        <span><strong>Activar auditoría</strong> — historial de cambios en todos los formatos del cliente.</span>
      </label>

      {msg && (
        <div className={`cv2-taimes__msg cv2-taimes__msg--${msg.type}`}>{msg.text}</div>
      )}

      <div style={{ marginTop: 16, textAlign: 'right' }}>
        <button className="cv2-btn cv2-btn--primary" onClick={save} disabled={saving}>
          {saving ? 'Guardando…' : 'Guardar'}
        </button>
      </div>
    </div>
  );
}

function ClientTaimesPanel({ client, clientId, onUpdated }) {
  const [apiKey, setApiKey] = useState(client.taimes_api_key || '');
  const [aiEnabled, setAiEnabled] = useState(!!client.ai_enabled);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const save = () => {
    setSaving(true);
    setMsg(null);
    const token = document.querySelector('[name="csrf-token"]')?.content;
    fetch(`/settings/clients_v2/${clientId}/taimes`, {
      method: 'PATCH',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': token, Accept: 'application/json' },
      body: JSON.stringify({ taimes_api_key: apiKey, ai_enabled: aiEnabled }),
    })
      .then(r => r.json().then(j => ({ ok: r.ok, j })))
      .then(({ ok, j }) => {
        if (ok && j.success) {
          setMsg({ type: 'ok', text: 'Guardado.' });
          if (onUpdated) onUpdated({ taimes_api_key: j.taimes_api_key, ai_enabled: j.ai_enabled });
        } else {
          setMsg({ type: 'err', text: (j && j.error) || 'No se pudo guardar.' });
        }
      })
      .catch(() => setMsg({ type: 'err', text: 'No se pudo guardar.' }))
      .finally(() => setSaving(false));
  };

  return (
    <div className="cv2-taimes">
      <h4 className="cv2-panel-title">Integración Taimes IA</h4>
      <p className="cv2-taimes__hint">
        Pega la API Key de Taimes (<code>kmz_…</code>) y activa la IA para habilitar la revisión con IA
        en los campos de archivo y matrices de los formatos de este cliente.
      </p>

      {/* Sin llave cargada, el camino normal ya no es pegarla a mano: es crear
          el tenant en Taimes desde acá y que la llave se carge sola. */}
      {!apiKey && (
        <CreateTaimesTenant
          clientId={clientId}
          onCreated={(data) => {
            setApiKey(data.taimes_api_key || '');
            if (onUpdated) onUpdated({ taimes_api_key: data.taimes_api_key });
          }}
        />
      )}

      <div className="cv2-field" style={{ marginBottom: 14 }}>
        <span className="cv2-field__label">API Key de Taimes</span>
        <input
          type="text"
          className="cv2-taimes__input"
          value={apiKey}
          onChange={e => setApiKey(e.target.value)}
          placeholder="kmz_..."
          style={{ fontFamily: 'monospace' }}
        />
      </div>

      <label className="cv2-taimes__toggle">
        <input type="checkbox" checked={aiEnabled} onChange={e => setAiEnabled(e.target.checked)} />
        <span><strong>Activar IA</strong> — muestra los botones de IA (campo archivo y matriz) a este cliente.</span>
      </label>

      {msg && (
        <div className={`cv2-taimes__msg cv2-taimes__msg--${msg.type}`}>{msg.text}</div>
      )}

      <div style={{ marginTop: 16, textAlign: 'right' }}>
        <button className="cv2-btn cv2-btn--primary" onClick={save} disabled={saving}>
          {saving ? 'Guardando…' : 'Guardar'}
        </button>
      </div>
    </div>
  );
}

// Alta del tenant del cliente EN Taimes, desde acá.
//
// Muestra PRIMERO lo que se va a enviar y recién crea cuando el admin confirma:
// esto da de alta una empresa en otro sistema, no es un guardar cualquiera. El
// detalle lo trae el servidor desde el mismo servicio que hace el envío, así que
// lo que se aprueba es literalmente lo que viaja.
function CreateTaimesTenant({ clientId, onCreated }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);

  const csrf = () => document.querySelector('[name="csrf-token"]')?.content;

  const load = () => {
    setLoading(true);
    setError(null);
    fetch(`/settings/clients_v2/${clientId}/taimes_preview`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then(r => r.json())
      .then(j => (j.error ? setError(j.error) : setPreview(j)))
      .catch(() => setError('No se pudo leer la información del cliente.'))
      .finally(() => setLoading(false));
  };

  const create = () => {
    setCreating(true);
    setError(null);
    fetch(`/settings/clients_v2/${clientId}/taimes_provision`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf(), Accept: 'application/json' },
    })
      .then(r => r.json().then(j => ({ ok: r.ok, j })))
      .then(({ ok, j }) => {
        if (!ok || j.error) { setError(j.error || 'No se pudo crear el tenant.'); return; }
        setDone(j);
        setPreview(null);
        if (onCreated) onCreated(j);
      })
      .catch(() => setError('No se pudo crear el tenant.'))
      .finally(() => setCreating(false));
  };

  if (done) {
    return (
      <div className="cv2-taimes__msg cv2-taimes__msg--ok" style={{ marginBottom: 14 }}>
        <strong>Tenant creado en Taimes.</strong>
        <div>Cliente: {done.tenant?.name}</div>
        {/* La contraseña se muestra UNA vez, acá: es la única forma de que
            quien da el alta sepa con qué entrar. */}
        <div>Usuario: <code>{done.admin?.email}</code> · Contraseña: <code>{done.admin?.password}</code></div>
        <div>La API Key quedó guardada abajo. La IA sigue apagada hasta que la actives.</div>
        {done.configured?.mcpServerMissing && (
          <div style={{ marginTop: 6 }}>
            ⚠️ El servidor MCP de beewo no está registrado en Taimes: el tenant quedó creado
            pero todavía no ve los datos del cliente.
          </div>
        )}
      </div>
    );
  }

  if (!preview) {
    return (
      <div style={{ marginBottom: 16 }}>
        <button className="cv2-btn cv2-btn--primary" onClick={load} disabled={loading}>
          {loading ? 'Cargando…' : 'Crear tenant de Taimes'}
        </button>
        {error && <div className="cv2-taimes__msg cv2-taimes__msg--err">{error}</div>}
      </div>
    );
  }

  const p = preview.payload || {};
  const bloqueado = !preview.partner_key_configured || (preview.missing || []).length > 0;

  return (
    <div className="cv2-taimes__msg" style={{ marginBottom: 16, textAlign: 'left' }}>
      <strong>Esto es lo que se va a enviar a Taimes:</strong>
      <ul style={{ margin: '8px 0 0 18px' }}>
        <li>Razón social: {p.companyName}</li>
        <li>NIT: {p.taxId || '—'}</li>
        <li>Correo de contacto: {p.contactEmail || '—'}</li>
        <li>Teléfono: {p.contactPhone || '—'}</li>
        <li>Dirección: {p.address || '—'}</li>
        <li>Plan: Free · queda como cliente administrado por Beewo</li>
        <li>Integración: {p.integration?.provider} en {p.integration?.baseUrl}</li>
      </ul>

      {(preview.missing || []).length > 0 && (
        <div style={{ marginTop: 8 }}>
          ⛔ Falta completar en el cliente: {preview.missing.join(', ')}. Sin esto no se puede crear.
        </div>
      )}
      {/* Avisos: se puede crear igual. Mezclarlos con los bloqueantes hace que
          el admin no sepa cuál lo está frenando de verdad. */}
      {(preview.warnings || []).map((w, i) => (
        <div key={i} style={{ marginTop: 6, opacity: 0.85 }}>ℹ️ {w}</div>
      ))}
      {!preview.partner_key_configured && (
        <div style={{ marginTop: 8 }}>
          ⚠️ Falta configurar <code>TAIMES_PARTNER_API_KEY</code> en el servidor de beewo.
        </div>
      )}
      {error && <div style={{ marginTop: 8 }}>{error}</div>}

      <div style={{ marginTop: 12, textAlign: 'right' }}>
        <button className="cv2-btn" onClick={() => setPreview(null)} disabled={creating}>
          Cancelar
        </button>{' '}
        <button className="cv2-btn cv2-btn--primary" onClick={create} disabled={creating || bloqueado}>
          {creating ? 'Creando…' : 'Crear en Taimes'}
        </button>
      </div>
    </div>
  );
}

function ClientInfoPanel({ client }) {
  return (
    <div className="cv2-panel-grid">
      <Field label="Empresa" value={client.company_name} />
      <Field label="NIT" value={client.nit} />
      <Field label="Teléfono" value={client.phone} />
      <Field label="Dirección" value={client.address} />
      <Field label="Web" value={client.web} />
      <Field label="URL" value={client.url_name} />
      <Field label="Admin email" value={client.user_owner_email} />
      <Field label="App type" value={client.app_type} />
      <Field label="Type user" value={client.type_user} />
      <Field label="Estado tenant" value={client.tenant_state} />
      <Field label="Estado registros" value={client.state_records} />
      <Field label="Multi-usuario" value={client.is_multiuser ? 'Sí' : 'No'} />
      <Field label="2FA" value={client.two_factor ? 'Sí' : 'No'} />
      <Field label="Demo desde" value={fmtDate(client.start_demo)} />
      <Field label="Demo hasta" value={fmtDate(client.end_demo)} />
      <Field label="Creado" value={fmtDate(client.created_at)} />
      {client.admin && (
        <Field
          label="Admin user"
          value={`${client.admin.first_name || ''} ${client.admin.first_last_name || ''} (${client.admin.email})`.trim()}
        />
      )}
    </div>
  );
}

function ClientPlanPanel({ client }) {
  if (!client.plan) return <p>Este cliente no tiene plan asignado.</p>;
  const { plan } = client;
  const used = {
    users: client.user_count_actual,
    records: client.records_count,
    formats: client.formats_count,
    storage: client.size_count,
  };
  return (
    <div>
      <h4 className="cv2-panel-title">Plan: {plan.name}</h4>
      <div className="cv2-quota-grid">
        <Quota label="Usuarios" used={used.users} limit={plan.users_plan} unit="" />
        <Quota label="Registros" used={used.records} limit={plan.records_plan} unit="" />
        <Quota label="Almacenamiento" used={used.storage} limit={plan.size_plan} unit="GB" />
        <Quota label="Tableros tareas" used={null} limit={plan.tasks_board_plan} unit="" />
      </div>
      <div className="cv2-panel-grid" style={{ marginTop: 16 }}>
        <Field label="Formatos creados" value={used.formats?.toLocaleString()} />
        <Field label="Surveys guardados" value={client.surveys_count_saved?.toLocaleString()} />
        <Field label="Tasks" value={client.tasks_count?.toLocaleString()} />
      </div>
    </div>
  );
}

function ClientSarlaftPanel({ client }) {
  if (!client.has_sarlaft) {
    return (
      <div className="cv2-empty-state">
        <i className="fas fa-shield-alt" />
        <p>SARLAFT no está activado para este cliente.</p>
      </div>
    );
  }
  return (
    <div className="cv2-panel-grid">
      <Field label="Cuota base" value={client.sarlaft_consultas?.toLocaleString()} />
      <Field label="Extras" value={client.sarlaft_consultas_extra?.toLocaleString()} />
      <Field label="Período" value={client.sarlaft_period} />
    </div>
  );
}

function ClientModulesPanel({ client }) {
  const mods = client.modules;
  if (!mods) return <p>Sin módulos configurados.</p>;
  if (Array.isArray(mods)) {
    return (
      <ul className="cv2-module-list">
        {mods.map((m, i) => <li key={i}>{typeof m === 'string' ? m : JSON.stringify(m)}</li>)}
      </ul>
    );
  }
  return (
    <ul className="cv2-module-list">
      {Object.entries(mods).map(([k, v]) => (
        <li key={k}>
          <strong>{k}:</strong> {String(v)}
        </li>
      ))}
    </ul>
  );
}

function Field({ label, value }) {
  return (
    <div className="cv2-field">
      <span className="cv2-field__label">{label}</span>
      <span className="cv2-field__value">{value || '—'}</span>
    </div>
  );
}

function Quota({ label, used, limit, unit }) {
  const u = Number(used) || 0;
  const l = Number(limit) || 0;
  const pct = l > 0 ? Math.min(100, Math.round((u / l) * 100)) : 0;
  const cls = pct >= 90 ? 'red' : pct >= 70 ? 'yellow' : 'green';
  return (
    <div className="cv2-quota">
      <div className="cv2-quota__head">
        <span className="cv2-quota__label">{label}</span>
        <span className="cv2-quota__value">
          {used != null ? u.toLocaleString() : '—'} <small>/ {l.toLocaleString()} {unit}</small>
        </span>
      </div>
      <div className="cv2-quota__bar">
        <div className={`cv2-quota__fill cv2-quota__fill--${cls}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="cv2-quota__pct">{pct}%</span>
    </div>
  );
}

function fmtDate(s) {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleDateString('es-CO', { year: 'numeric', month: 'short', day: '2-digit' });
  } catch { return '—'; }
}
